<script setup lang="ts">
import { computed, onUnmounted, ref, watch, type PropType } from 'vue';
// NInputNumber is imported so vue-tsc can type its inline @update:value
// handler in the template (the value param would otherwise be `any`).
import { type SelectOption } from 'naive-ui';
import {
  getVaultFileMetadata,
  PreprocessingTaskStatus,
  SignatureFieldType,
  SigningFileType,
  SigningRequestSigneeRole,
  type VaultFileRevisionInDb,
} from '@/api';
import GaiaBanner from '../composite/GaiaBanner.vue';
import GaiaCard from '../base/GaiaCard.vue';
import GaiaButton from '../base/GaiaButton.vue';
import GaiaIconButton from '../base/GaiaIconButton.vue';
import { IconChevronDown, IconChevronUp, IconClose } from '@/components/design/icons';
import GaiaInput from '../base/inputs/GaiaInput.vue';
import GaiaLink from '../base/GaiaLink.vue';
import GaiaPaletteItem from './GaiaPaletteItem.vue';
import GaiaSelect from '../base/selects/GaiaSelect.vue';
import GaiaSignatureFieldCanvas from './GaiaSignatureFieldCanvas.vue';
import GaiaStatusChip from '../base/GaiaStatusChip.vue';
import GaiaText from '../base/GaiaText.vue';
import GaiaTooltip from '../base/GaiaTooltip.vue';
import GaiaVaultFilePdfPreview from '../domain/fileVault/GaiaVaultFilePdfPreview.vue';
import { signatureFieldTypeMap } from '../statusMaps';
import { gaiaNeutral, gaiaShadow, gaiaSignerPalette } from '../themes/tokens';
import { useVaultWebSocket } from '@/composables/useVaultWebSocket';
import type {
  SignatureDraftField,
  SignatureFieldPaletteItem,
  SignatureReviewForm,
  SignatureReviewParty,
  SignatureUploadDocumentForm,
} from '@/types/contractManagement/signatureFlow';
import GaiaInputNumber from '../base/inputs/GaiaInputNumber.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import {
  DEFAULT_FIELD_FONT_SIZE_PT,
  FIELD_FONT_SIZES_PT,
  fieldFit,
  fieldPreviewText,
  isTextStyleField,
} from '@/utils/signatureFieldText';

/**
 * Step 4 of the prepare-signature wizard: place the fields each signer fills.
 *
 * The document on the left, the palette, signers, placed fields and the
 * selected field's settings on the right. The document is the app's shared
 * `GaiaVaultFilePdfPreview`, scrolled as one column; every page carries its own
 * `GaiaSignatureFieldCanvas` as the placement surface.
 *
 * Only signers can own a field, so the palette's Signature entry is inert
 * until a signer is the active recipient.
 */

const DEFAULT_TEXT_MAX_LENGTH = 500;
/** If preprocessing never resolves, stop waiting after this and say so. */
const PREVIEW_STATUS_TIMEOUT_MS = 3 * 60 * 1000;

const props = defineProps({
  form: {
    type: Object as PropType<SignatureReviewForm>,
    required: true,
  },
  parties: {
    type: Array as PropType<SignatureReviewParty[]>,
    required: true,
  },
  documents: {
    type: Array as PropType<SignatureUploadDocumentForm[]>,
    required: true,
  },
  orderedSigners: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ 'update:form': [form: SignatureReviewForm] }>();

const message = useGaiaToast();

const previewRef = ref<InstanceType<typeof GaiaVaultFilePdfPreview>>();
const previewRevisionUid = ref<string | null>(null);
const previewStatusMessage = ref<string | null>(null);
const previewStatusTone = ref<'info' | 'warning' | 'error'>('info');
let previewStatusRequestId = 0;
let previewStatusTimeoutHandle: ReturnType<typeof setTimeout> | null = null;

// ── Palette ─────────────────────────────────────────────────────────────────
//
// "City" and "Capacity" are Text fields with a preset question (the signer
// types the answer), so palette entries are identified by label, not type.
const FIELD_PALETTE: SignatureFieldPaletteItem[] = [
  {
    type: SignatureFieldType.SIGNATURE,
    label: signatureFieldTypeMap[SignatureFieldType.SIGNATURE],
  },
  { type: SignatureFieldType.CHECKBOX, label: signatureFieldTypeMap[SignatureFieldType.CHECKBOX] },
  { type: SignatureFieldType.MENTION, label: signatureFieldTypeMap[SignatureFieldType.MENTION] },
  { type: SignatureFieldType.TEXT, label: signatureFieldTypeMap[SignatureFieldType.TEXT] },
  { type: SignatureFieldType.TEXT, label: 'City', presetQuestion: 'City' },
  // The signer's title or role — "Managing Director", "Authorised signatory".
  { type: SignatureFieldType.TEXT, label: 'Capacity', presetQuestion: 'Capacity' },
  {
    type: SignatureFieldType.SIGNATURE_DATE,
    label: signatureFieldTypeMap[SignatureFieldType.SIGNATURE_DATE],
  },
  {
    type: SignatureFieldType.SIGNER_NAME,
    label: signatureFieldTypeMap[SignatureFieldType.SIGNER_NAME],
  },
  {
    type: SignatureFieldType.SIGNER_EMAIL,
    label: signatureFieldTypeMap[SignatureFieldType.SIGNER_EMAIL],
  },
  {
    type: SignatureFieldType.READ_ONLY_TEXT,
    label: signatureFieldTypeMap[SignatureFieldType.READ_ONLY_TEXT],
  },
];

/**
 * The mock's 16×16 stroke glyph per palette entry, keyed by the entry's label
 * ("City" and "Capacity" are Text fields with a preset question, so the type
 * alone can't pick them). Drawn in the palette, the set-fields list and the selected field.
 */
const FIELD_ICON_PATHS: Partial<Record<string, string>> = {
  [signatureFieldTypeMap[SignatureFieldType.SIGNATURE]]: 'M1.5 11c2 0 3-8 5-8s2 8 4 8 3-4 4-4',
  [signatureFieldTypeMap[SignatureFieldType.CHECKBOX]]: 'M2.5 2.5h11v11h-11z M5.2 8l2.3 2.3L11 5.6',
  [signatureFieldTypeMap[SignatureFieldType.MENTION]]:
    'M8 5.4a2.6 2.6 0 1 0 0 5.2 2.6 2.6 0 0 0 0-5.2 M10.6 8v1.4a2 2 0 0 0 3.3-1.4A6 6 0 1 0 11.4 13',
  [signatureFieldTypeMap[SignatureFieldType.TEXT]]: 'M3 3.5h10 M8 3.5V13',
  City: 'M2 13.5h12 M4 13.5V6l4-3 4 3v7.5 M7 13.5v-3h2v3',
  Capacity: 'M2.5 5.5h11v7.5h-11z M6 5.5V3.5h4v2 M2.5 9h11',
  [signatureFieldTypeMap[SignatureFieldType.SIGNATURE_DATE]]:
    'M2.5 4h11v9.5h-11z M2.5 7h11 M5.5 2.5v2 M10.5 2.5v2',
  [signatureFieldTypeMap[SignatureFieldType.SIGNER_NAME]]:
    'M8 8.6a2.6 2.6 0 1 0 0-5.2 2.6 2.6 0 0 0 0 5.2 M3 13.5c0-2.4 2.2-4 5-4s5 1.6 5 4',
  [signatureFieldTypeMap[SignatureFieldType.SIGNER_EMAIL]]: 'M2.5 4h11v8h-11z M2.5 4.5 8 9l5.5-4.5',
  [signatureFieldTypeMap[SignatureFieldType.READ_ONLY_TEXT]]:
    'M4.5 7V5.2a3.5 3.5 0 0 1 7 0V7 M3.5 7h9v6.5h-9z',
};

function iconPathFor(label: string): string {
  return FIELD_ICON_PATHS[label] ?? FIELD_ICON_PATHS.Text ?? '';
}

const FONT_SIZE_OPTIONS: SelectOption[] = FIELD_FONT_SIZES_PT.map(size => ({
  label: `${size} pt`,
  value: size,
}));

const NAME_FORMAT_OPTIONS: SelectOption[] = [
  { label: 'Full name', value: 'full_name' },
  { label: 'First name', value: 'first_name' },
  { label: 'Last name', value: 'last_name' },
];

/** Signature is armed by default — it is the field every signer needs. */
const activeFieldType = ref<SignatureFieldPaletteItem | null>(FIELD_PALETTE[0]);

// ── Derived state ───────────────────────────────────────────────────────────

/** The header rule's label — the mock's "N field(s) placed". */
const placedLabel = computed(() => {
  const count = props.form.fields.length;
  return `${count} field${count === 1 ? '' : 's'} placed`;
});

const selectedField = computed(
  () => props.form.fields.find(field => field.uid === props.form.selected_field_uid) ?? null
);

const activeParty = computed(
  () => props.parties.find(party => party.uid === props.form.active_party_uid) ?? null
);

const canAssignSignatureField = computed(
  () => activeParty.value?.role === SigningRequestSigneeRole.SIGNER
);

const signerParties = computed(() =>
  props.parties.filter(party => party.role === SigningRequestSigneeRole.SIGNER)
);

const signableDocuments = computed(() =>
  props.documents.filter(
    document => document.signing_file_type === SigningFileType.SIGNABLE_DOCUMENT
  )
);

// A document still without a signature says so in the picker, so the one to
// switch to is found where switching happens.
const documentSelectOptions = computed<SelectOption[]>(() =>
  signableDocuments.value.map(document => ({
    label: documentsMissingSignatureField.value.includes(document)
      ? `${documentLabel(document)} · needs a signature`
      : documentLabel(document),
    value: document.vault_file.uid,
  }))
);

/**
 * What is still missing, as one chip in the header row — the signer list and
 * the document picker each mark their own, so a banner per gap repeated them
 * at the cost of the page's height. The chip's tooltip names them.
 */
const missingSummary = computed<string | null>(() => {
  const signers = signersMissingSignatureField.value.length;
  const documents = documentsMissingSignatureField.value.length;
  if (signers === 0 && documents === 0) return null;
  const parts = [
    signers > 0 ? `${signers} signer${signers === 1 ? '' : 's'}` : null,
    documents > 0 ? `${documents} document${documents === 1 ? '' : 's'}` : null,
  ].filter(Boolean);
  return `${parts.join(' · ')} without a signature`;
});

function documentLabel(document: SignatureUploadDocumentForm): string {
  return document.vault_file.current_revision?.file_name ?? `Vault file ${document.vault_file.uid}`;
}

// The revision the request is pinned to for the active document — what fields
// are stamped with and what the canvas must render, which is not necessarily
// the newest revision of the file.
function activeDocumentRevision(): VaultFileRevisionInDb | null {
  if (!props.form.active_document_vault_file_uid) return null;

  return (
    props.documents.find(
      document => document.vault_file.uid === props.form.active_document_vault_file_uid
    )?.vault_file.current_revision ?? null
  );
}

function activeDocumentRevisionUid(): string | null {
  return activeDocumentRevision()?.uid ?? null;
}

// Status updates arrive per file, so they can carry a revision this request is
// not using — the file gained one after the fields were placed. Only the
// pinned revision may drive the canvas; anything else falls back to it with
// the status we already hold.
function resolveAgainstPinnedRevision(
  incoming: VaultFileRevisionInDb | null
): VaultFileRevisionInDb | null {
  const pinned = activeDocumentRevision();
  if (!pinned) return incoming;

  return incoming?.uid === pinned.uid ? incoming : pinned;
}

const fieldsForCurrentDocument = computed(() => {
  const revisionUid = activeDocumentRevisionUid();
  if (!revisionUid) return [];

  return props.form.fields.filter(field => field.vault_file_revision_uid === revisionUid);
});

/** The document on screen's fields, grouped by page in reading order. */
const placedGroups = computed(() => {
  const sorted = [...fieldsForCurrentDocument.value].sort((left, right) => {
    if (left.page !== right.page) return left.page - right.page;
    if (left.y !== right.y) return left.y - right.y;
    return left.x - right.x;
  });
  const groups: { page: number; fields: SignatureDraftField[] }[] = [];
  for (const field of sorted) {
    const last = groups.at(-1);
    if (last?.page === field.page) last.fields.push(field);
    else groups.push({ page: field.page, fields: [field] });
  }
  return groups;
});

/** City, Capacity: a Text field whose question is the field itself, not a setting. */
function isPresetText(field: SignatureDraftField): boolean {
  return FIELD_PALETTE.some(
    item => item.presetQuestion !== undefined && item.presetQuestion === field.question
  );
}

/** Whether a selected field has anything to set, so it unfolds only when it does. */
function fieldHasSettings(field: SignatureDraftField): boolean {
  return (
    signerParties.value.length > 1 ||
    isTextStyleField(field.type) ||
    field.type === SignatureFieldType.MENTION ||
    field.type === SignatureFieldType.TEXT ||
    field.type === SignatureFieldType.READ_ONLY_TEXT ||
    field.type === SignatureFieldType.SIGNER_NAME
  );
}

// ── Signer identity ─────────────────────────────────────────────────────────

const signerOrderByUid = computed(
  () => new Map(signerParties.value.map((party, index) => [party.uid, index + 1]))
);

function partyBaseName(party: SignatureReviewParty): string {
  return party.name || 'Unnamed signer';
}

function partyDisplayName(party: SignatureReviewParty): string {
  const base = partyBaseName(party);
  if (!props.orderedSigners || party.role !== SigningRequestSigneeRole.SIGNER) return base;

  const order = signerOrderByUid.value.get(party.uid);
  return order ? `${order}. ${base}` : base;
}

/** Positional colour, matching what the canvas draws. */
function partyColor(partyUid: string | null | undefined): string {
  if (!partyUid) return gaiaNeutral.textFaint;

  const index = props.parties.findIndex(party => party.uid === partyUid);
  return index === -1 ? gaiaNeutral.textFaint : gaiaSignerPalette[index % gaiaSignerPalette.length];
}

const signerSelectOptions = computed<SelectOption[]>(() =>
  signerParties.value.map(party => ({ label: partyDisplayName(party), value: party.uid }))
);

function fieldRecipientName(field: SignatureDraftField): string {
  const party = props.parties.find(entry => entry.uid === field.recipient_id);
  return party ? partyBaseName(party) : 'Unassigned';
}

/** The palette entry a placed field came from, so "City" reads as City. */
function fieldPaletteLabel(field: SignatureDraftField): string {
  const match =
    FIELD_PALETTE.find(
      item => item.type === field.type && item.presetQuestion === field.question
    ) ?? FIELD_PALETTE.find(item => item.type === field.type);

  return match?.label ?? signatureFieldTypeMap[field.type];
}

// ── Warnings ────────────────────────────────────────────────────────────────

const signersMissingSignatureField = computed(() => {
  const withField = new Set(
    props.form.fields
      .filter(field => field.type === SignatureFieldType.SIGNATURE)
      .map(field => field.recipient_id)
      .filter((uid): uid is string => typeof uid === 'string' && uid.length > 0)
  );

  return signerParties.value.filter(party => !withField.has(party.uid));
});

const documentsMissingSignatureField = computed(() => {
  const withField = new Set(
    props.form.fields
      .filter(field => field.type === SignatureFieldType.SIGNATURE)
      .map(field => field.vault_file_revision_uid)
      .filter((uid): uid is string => typeof uid === 'string' && uid.length > 0)
  );

  return signableDocuments.value.filter(document => {
    const revisionUid = document.vault_file.current_revision?.uid;
    return !revisionUid || !withField.has(revisionUid);
  });
});

// ── Palette interaction ─────────────────────────────────────────────────────

function isPaletteItemDisabled(item: SignatureFieldPaletteItem): boolean {
  return item.type === SignatureFieldType.SIGNATURE && !canAssignSignatureField.value;
}

function isPaletteItemArmed(item: SignatureFieldPaletteItem): boolean {
  return activeFieldType.value?.label === item.label;
}

// The fields almost every signer gets are always in view; the rest wait
// behind "More fields" — which stays open while one of them is armed.
const PRIMARY_FIELD_LABELS = new Set([
  signatureFieldTypeMap[SignatureFieldType.SIGNATURE],
  signatureFieldTypeMap[SignatureFieldType.SIGNATURE_DATE],
  signatureFieldTypeMap[SignatureFieldType.SIGNER_NAME],
  'City',
]);
const primaryPalette = FIELD_PALETTE.filter(item => PRIMARY_FIELD_LABELS.has(item.label)).sort(
  (a, b) => [...PRIMARY_FIELD_LABELS].indexOf(a.label) - [...PRIMARY_FIELD_LABELS].indexOf(b.label)
);
const morePalette = FIELD_PALETTE.filter(item => !PRIMARY_FIELD_LABELS.has(item.label));
const showMoreFields = ref(false);
const moreFieldsOpen = computed(
  () => showMoreFields.value || morePalette.some(item => isPaletteItemArmed(item))
);
const visiblePalette = computed(() =>
  moreFieldsOpen.value ? [...primaryPalette, ...morePalette] : primaryPalette
);

/** Under a signer's name: what they have so far, or that a signature is still missing. */
function signerMeta(party: SignatureReviewParty): string {
  if (signersMissingSignatureField.value.some(missing => missing.uid === party.uid)) {
    return 'Needs a signature';
  }
  const count = props.form.fields.filter(field => field.recipient_id === party.uid).length;
  return count === 1 ? '1 field' : `${count} fields`;
}

function togglePaletteItem(item: SignatureFieldPaletteItem): void {
  if (isPaletteItemDisabled(item)) return;

  activeFieldType.value = isPaletteItemArmed(item) ? null : item;
}

function onPaletteDragStart(event: DragEvent, item: SignatureFieldPaletteItem): void {
  if (isPaletteItemDisabled(item)) {
    event.preventDefault();
    return;
  }

  event.dataTransfer?.setData('fieldType', item.type);
  if (item.presetQuestion) {
    event.dataTransfer?.setData('presetQuestion', item.presetQuestion);
  }
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'copy';
  }
}

// ── Form updates ────────────────────────────────────────────────────────────

function updateForm(patch: Partial<SignatureReviewForm>): void {
  emit('update:form', { ...props.form, ...patch });
}

function createDraftFieldUid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `draft-field-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Each field type carries its own settings, and the provider rejects a field
 * that arrives without them — so the defaults are set at creation.
 */
function createDraftField(payload: {
  type: SignatureFieldType;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  presetQuestion?: string;
  recipientUid: string;
  vaultFileRevisionUid: string;
}): SignatureDraftField {
  const common = {
    uid: createDraftFieldUid(),
    page: payload.page,
    x: payload.x,
    y: payload.y,
    width: payload.width,
    height: payload.height,
    recipient_id: payload.recipientUid,
    vault_file_revision_uid: payload.vaultFileRevisionUid,
    ...(isTextStyleField(payload.type) ? { font_size: DEFAULT_FIELD_FONT_SIZE_PT } : {}),
  };

  switch (payload.type) {
    case SignatureFieldType.SIGNATURE:
      return { ...common, type: SignatureFieldType.SIGNATURE, reason: null };
    case SignatureFieldType.CHECKBOX:
      return {
        ...common,
        type: SignatureFieldType.CHECKBOX,
        size: Math.max(1, Math.round(Math.min(payload.width, payload.height))),
        optional: false,
        name: null,
      };
    case SignatureFieldType.MENTION:
      return { ...common, type: SignatureFieldType.MENTION, mention: 'Mention', name: null };
    case SignatureFieldType.TEXT:
      return {
        ...common,
        type: SignatureFieldType.TEXT,
        question: payload.presetQuestion ?? 'Text',
        max_length: DEFAULT_TEXT_MAX_LENGTH,
        instruction: null,
        optional: false,
        name: null,
        default_value: null,
        read_only: false,
      };
    case SignatureFieldType.SIGNATURE_DATE:
      return { ...common, type: SignatureFieldType.SIGNATURE_DATE, name: null };
    case SignatureFieldType.SIGNER_NAME:
      return {
        ...common,
        type: SignatureFieldType.SIGNER_NAME,
        name_format: 'full_name',
        name: null,
      };
    case SignatureFieldType.SIGNER_EMAIL:
      return { ...common, type: SignatureFieldType.SIGNER_EMAIL, name: null };
    case SignatureFieldType.READ_ONLY_TEXT:
      return { ...common, type: SignatureFieldType.READ_ONLY_TEXT, text: 'Text' };
  }
}

function handleFieldDropped(payload: {
  type: SignatureFieldType;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  presetQuestion?: string;
}): void {
  const party = activeParty.value;

  if (!party) {
    message.error('Select a signer before placing a field.');
    return;
  }

  if (party.role !== SigningRequestSigneeRole.SIGNER) {
    message.error('Fields can only be assigned to signers.');
    return;
  }

  const revisionUid = activeDocumentRevisionUid();
  if (!revisionUid) {
    message.error('Select a signable document before placing a field.');
    return;
  }

  const created = createDraftField({
    ...payload,
    recipientUid: party.uid,
    vaultFileRevisionUid: revisionUid,
  });

  updateForm({ fields: [...props.form.fields, created], selected_field_uid: created.uid });
}

function handleFieldMoved(payload: { uid: string; x: number; y: number; page: number }): void {
  updateForm({
    fields: props.form.fields.map(field =>
      field.uid === payload.uid
        ? { ...field, x: payload.x, y: payload.y, page: payload.page }
        : field
    ),
  });
}

function handleFieldResized(payload: { uid: string; width: number; height: number }): void {
  updateForm({
    fields: props.form.fields.map(field =>
      field.uid === payload.uid ? { ...field, width: payload.width, height: payload.height } : field
    ),
  });
}

function removeField(uid: string): void {
  updateForm({
    fields: props.form.fields.filter(field => field.uid !== uid),
    selected_field_uid:
      props.form.selected_field_uid === uid ? null : props.form.selected_field_uid,
  });
}

/** Selecting from the list also scrolls the field itself into view. */
function selectField(uid: string | null): void {
  updateForm({ selected_field_uid: uid });
  const field = props.form.fields.find(entry => entry.uid === uid);
  if (field) {
    previewRef.value?.goToPage(field.page, { x: field.x, y: field.y });
  }
}

// ── Text preview & fit ──────────────────────────────────────────────────────

/**
 * Answers the author typed to see how a Text field would look, by field uid.
 * Preview only: never part of the field, so never saved or sent.
 */
const previewSamples = ref<Record<string, string>>({});

function setPreviewSample(uid: string, value: string): void {
  previewSamples.value = { ...previewSamples.value, [uid]: value };
}

/** The active document's page sizes in PDF points, as each page's canvas measures them. */
const pageSizesPts = ref<Map<number, { widthPts: number; heightPts: number }>>(new Map());

function onPageMeasured(page: number, size: { widthPts: number; heightPts: number }): void {
  pageSizesPts.value = new Map(pageSizesPts.value).set(page, size);
}

/** The stock preview for the selected field — the placeholder of its "Preview with" box. */
function defaultPreview(field: SignatureDraftField): string {
  const party = props.parties.find(entry => entry.uid === field.recipient_id);
  return fieldPreviewText(field, party)?.text ?? '';
}

/**
 * How the selected field's text sits in its box: one line under its settings,
 * with the fix when there is one. Null for fields without text, or before the
 * page is measured.
 */
const selectedFit = computed<{
  message: string;
  warn: boolean;
  action?: { label: string; patch: Partial<SignatureDraftField> };
} | null>(() => {
  const field = selectedField.value;
  if (!field) return null;
  const party = props.parties.find(entry => entry.uid === field.recipient_id);
  const preview = fieldPreviewText(field, party, previewSamples.value[field.uid]);
  const page = pageSizesPts.value.get(field.page);
  if (!preview || !page) return null;

  const size = field.font_size ?? DEFAULT_FIELD_FONT_SIZE_PT;
  const fit = fieldFit(
    preview.text,
    size,
    ((field.width ?? 20) / 100) * page.widthPts,
    ((field.height ?? 8) / 100) * page.heightPts
  );
  const room = fit.capacity.total;

  if (!fit.fits) {
    return {
      message: `“${preview.text}” doesn’t fit at ${size} pt — enlarge the box${
        fit.fittingSizePt ? ' or use a smaller size' : ''
      }.`,
      warn: true,
      action: fit.fittingSizePt
        ? { label: `Use ${fit.fittingSizePt} pt`, patch: { font_size: fit.fittingSizePt } }
        : undefined,
    };
  }
  if (field.type !== SignatureFieldType.TEXT) return null;

  const maxLength = field.max_length ?? DEFAULT_TEXT_MAX_LENGTH;
  if (maxLength > room) {
    return {
      message: `Room for about ${room} characters; longer answers get cut off.`,
      warn: true,
      action: room > 0 ? { label: `Limit to ${room}`, patch: { max_length: room } } : undefined,
    };
  }
  return { message: `Room for about ${room} characters at ${size} pt.`, warn: false };
});

function setActiveParty(uid: string | null): void {
  updateForm({ active_party_uid: uid });
}

function updateSelectedField(patch: Partial<SignatureDraftField>): void {
  const field = selectedField.value;
  if (!field) return;

  updateForm({
    fields: props.form.fields.map(entry =>
      entry.uid === field.uid ? { ...entry, ...patch } : entry
    ),
  });
}

function reassignSelectedField(recipientUid: string | null): void {
  const field = selectedField.value;
  if (!field) return;

  updateForm({
    fields: props.form.fields.map(entry =>
      entry.uid === field.uid ? { ...entry, recipient_id: recipientUid } : entry
    ),
    active_party_uid: recipientUid,
  });
}

/** Switching document drops a selection that lives on the old one. */
function setActiveDocument(vaultFileUid: string | null): void {
  // Its pages are other pages; their canvases measure them afresh.
  pageSizesPts.value = new Map();
  const nextRevisionUid =
    props.documents.find(document => document.vault_file.uid === vaultFileUid)?.vault_file
      .current_revision?.uid ?? null;

  const keepsSelection = props.form.fields.some(
    field =>
      field.uid === props.form.selected_field_uid &&
      field.vault_file_revision_uid === nextRevisionUid
  );

  updateForm({
    active_document_vault_file_uid: vaultFileUid,
    selected_field_uid: keepsSelection ? props.form.selected_field_uid : null,
  });
}

// ── Preview status ──────────────────────────────────────────────────────────

function clearPreviewStatusTimeout(): void {
  if (previewStatusTimeoutHandle) {
    clearTimeout(previewStatusTimeoutHandle);
    previewStatusTimeoutHandle = null;
  }
}

function setPreviewStatus(options: {
  message: string | null;
  tone?: 'info' | 'warning' | 'error';
  revisionUid?: string | null;
}): void {
  previewStatusMessage.value = options.message;
  previewStatusTone.value = options.tone ?? 'info';
  previewRevisionUid.value = options.revisionUid ?? null;
}

// Safety net: preprocessing that never reports a terminal state would
// otherwise leave the pane waiting forever.
function startPreviewStatusTimeout(requestId: number): void {
  clearPreviewStatusTimeout();
  previewStatusTimeoutHandle = setTimeout(() => {
    previewStatusTimeoutHandle = null;
    if (requestId !== previewStatusRequestId) return;

    setPreviewStatus({ message: 'There was an error in processing the document.', tone: 'error' });
  }, PREVIEW_STATUS_TIMEOUT_MS);
}

function applyPreviewRevision(revision: VaultFileRevisionInDb | null): void {
  if (!revision) {
    clearPreviewStatusTimeout();
    setPreviewStatus({
      message: 'The selected document is missing a current revision.',
      tone: 'error',
    });
    return;
  }

  switch (revision.preview_status) {
    case PreprocessingTaskStatus.DONE:
      clearPreviewStatusTimeout();
      setPreviewStatus({ message: null, revisionUid: revision.uid });
      return;
    case PreprocessingTaskStatus.PENDING:
    case PreprocessingTaskStatus.RUNNING:
      // Leave the timeout running — it bounds the total wait.
      setPreviewStatus({ message: 'The file is still being processed.', tone: 'info' });
      return;
    default:
      clearPreviewStatusTimeout();
      setPreviewStatus({
        message: 'There was an error in processing the document.',
        tone: 'error',
      });
  }
}

async function refreshActiveDocumentPreview(): Promise<void> {
  previewStatusRequestId += 1;
  const requestId = previewStatusRequestId;
  clearPreviewStatusTimeout();

  const activeVaultFileUid = props.form.active_document_vault_file_uid;
  if (!activeVaultFileUid) {
    setPreviewStatus({ message: null });
    return;
  }

  setPreviewStatus({ message: 'Checking document processing status…', tone: 'info' });
  startPreviewStatusTimeout(requestId);

  try {
    const response = await getVaultFileMetadata({ path: { file_uid: activeVaultFileUid } });
    if (requestId !== previewStatusRequestId) return;

    applyPreviewRevision(resolveAgainstPinnedRevision(response.data?.current_revision ?? null));
  } catch {
    if (requestId !== previewStatusRequestId) return;

    setPreviewStatus({ message: 'There was an error in processing the document.', tone: 'error' });
  }
}

// Keyed on the pinned revision, not just the document: switching documents
// changes it, and so does the user adopting a newer revision for the document
// they are already on.
watch(
  () => activeDocumentRevisionUid(),
  () => void refreshActiveDocumentPreview(),
  { immediate: true }
);

// Live preview-status updates for the active document.
useVaultWebSocket({
  fileUids: () =>
    props.form.active_document_vault_file_uid ? [props.form.active_document_vault_file_uid] : [],
  onFileUpdated: (uid, fileData) => {
    if (uid !== props.form.active_document_vault_file_uid) return;

    applyPreviewRevision(
      resolveAgainstPinnedRevision(
        (fileData as unknown as { current_revision?: VaultFileRevisionInDb }).current_revision ??
          null
      )
    );
  },
});

onUnmounted(() => {
  previewStatusRequestId += 1;
  clearPreviewStatusTimeout();
});
</script>

<template>
  <n-flex vertical :size="14" :wrap="false" class="fields-step">
    <!-- One row above the page: which document, and how far placing has got.
         What is still missing is the warning chip; the signer list and the
         document picker mark their own gaps. -->
    <n-flex align="center" justify="space-between" :size="10">
      <gaia-select
        :value="props.form.active_document_vault_file_uid"
        :options="documentSelectOptions"
        size="small"
        placeholder="Select a signable document"
        :style="{ flex: '1 1 260px', maxWidth: '460px' }"
        @update:value="setActiveDocument"
      />
      <n-flex :size="8">
        <gaia-status-chip :tone="props.form.fields.length > 0 ? 'success' : 'warning'">
          {{ placedLabel }}
        </gaia-status-chip>
        <gaia-tooltip v-if="missingSummary" placement="bottom-end" :max-width="320">
          <template #trigger>
            <gaia-status-chip tone="warning" :style="{ cursor: 'help' }">
              {{ missingSummary }}
            </gaia-status-chip>
          </template>
          <template v-if="signersMissingSignatureField.length > 0">
            Signers: {{ signersMissingSignatureField.map(partyBaseName).join(', ') }}.
          </template>
          <template v-if="documentsMissingSignatureField.length > 0">
            Documents: {{ documentsMissingSignatureField.map(documentLabel).join(', ') }}.
          </template>
          Pick a signer, then place a Signature field on each document.
        </gaia-tooltip>
        <gaia-status-chip v-else tone="success"
          >Every signer and document is signed for</gaia-status-chip
        >
      </n-flex>
    </n-flex>

    <!-- The one gap the sidebar can't show: nobody to place fields for. -->
    <gaia-banner v-if="signerParties.length === 0" type="warning">
      Add at least one signer before continuing.
    </gaia-banner>

    <!-- Page left, one sidebar right. The breakpoint is measured against this
         grid's own width (naive's `responsive` defaults to "self"); at the
         step's column it always fires, and the two stack only when the window
         is genuinely narrow. The grid takes the rest of the step's height, and
         the document and the sidebar each scroll on their own. Sized inline:
         with `item-responsive` naive renders the grid inside a resize observer
         Fragment, so this component's scoped styles never reach its element. -->
    <n-grid
      cols="1 760:24"
      :x-gap="14"
      :y-gap="14"
      item-responsive
      :style="{ flex: 1, minHeight: 0, gridAutoRows: 'minmax(0, 1fr)' }"
    >
      <n-gi span="1 760:18" class="fields-pane">
        <n-flex vertical :size="10" :wrap="false" :style="{ height: '100%' }">
          <div class="page-frame">
            <gaia-text
              v-if="!props.form.active_document_vault_file_uid"
              size="body"
              tone="tertiary"
              :style="{ padding: '48px', textAlign: 'center', display: 'block' }"
            >
              No signable document selected.
            </gaia-text>
            <gaia-banner
              v-else-if="previewStatusMessage"
              :type="previewStatusTone"
              :style="{ margin: '18px' }"
            >
              {{ previewStatusMessage }}
            </gaia-banner>
            <!-- The whole document as one scrolling column, with a field canvas
                 on every page. The status is resolved above against the pinned
                 revision, so the preview is only mounted once it is ready. -->
            <gaia-vault-file-pdf-preview
              v-else-if="previewRevisionUid"
              ref="previewRef"
              mode="continuous"
              zoomable
              :default-zoom="0.75"
              :revision-uid="previewRevisionUid"
              revision-preview-status="done"
            >
              <template #page-overlay="{ page, widthPts }">
                <gaia-signature-field-canvas
                  :fields="fieldsForCurrentDocument"
                  :parties="props.parties"
                  :selected-field-uid="props.form.selected_field_uid"
                  :current-page="page"
                  :page-width-pts="widthPts"
                  :samples="previewSamples"
                  :active-field-type="activeFieldType"
                  @page-measured="size => onPageMeasured(page, size)"
                  @field-dropped="payload => handleFieldDropped({ ...payload, page })"
                  @field-moved="handleFieldMoved"
                  @field-resized="handleFieldResized"
                  @field-removed="removeField"
                  @field-clicked="
                    uid =>
                      updateForm({
                        selected_field_uid: props.form.selected_field_uid === uid ? null : uid,
                      })
                  "
                />
              </template>
            </gaia-vault-file-pdf-preview>
          </div>
        </n-flex>
      </n-gi>

      <!-- The sidebar: four cards, each a bold title, its rows, a faint hint. -->
      <n-gi span="1 760:6" class="fields-sidebar">
        <n-flex vertical :size="10" :wrap="false">
          <!-- Who first, then what: a field goes to the signer picked here. -->
          <gaia-card content-style="padding: 12px">
            <n-flex vertical :size="8" :wrap="false">
              <gaia-text size="dense" weight="bold" tone="ink">Signers</gaia-text>
              <gaia-text v-if="signerParties.length === 0" size="micro" tone="faint">
                No signers added yet.
              </gaia-text>
              <n-flex v-else vertical :size="5" :wrap="false">
                <gaia-palette-item
                  v-for="party in signerParties"
                  :key="party.uid"
                  data-tour="signing.field-signer-chip"
                  :label="partyDisplayName(party)"
                  :meta="signerMeta(party)"
                  :meta-tone="
                    signersMissingSignatureField.some(missing => missing.uid === party.uid)
                      ? 'warning'
                      : 'subtle'
                  "
                  :dot="partyColor(party.uid)"
                  :active="props.form.active_party_uid === party.uid"
                  @select="
                    setActiveParty(props.form.active_party_uid === party.uid ? null : party.uid)
                  "
                />
              </n-flex>
            </n-flex>
          </gaia-card>

          <gaia-card content-style="padding: 12px">
            <n-flex vertical :size="8" :wrap="false">
              <gaia-text size="dense" weight="bold" tone="ink">Fields</gaia-text>
              <n-grid :cols="2" :x-gap="5" :y-gap="5">
                <n-gi v-for="item in visiblePalette" :key="item.label">
                  <gaia-palette-item
                    draggable
                    :data-tour="`signing.field-palette-${item.type}`"
                    :label="item.label"
                    :active="isPaletteItemArmed(item)"
                    :style="isPaletteItemDisabled(item) ? { opacity: 0.45 } : undefined"
                    @dragstart="(event: DragEvent) => onPaletteDragStart(event, item)"
                    @select="togglePaletteItem(item)"
                  >
                    <template #icon>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.3"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path :d="iconPathFor(item.label)" />
                      </svg>
                    </template>
                  </gaia-palette-item>
                </n-gi>
              </n-grid>
              <gaia-button
                v-if="!morePalette.some(item => isPaletteItemArmed(item))"
                size="tiny"
                quaternary
                icon-placement="right"
                :style="{ alignSelf: 'flex-start' }"
                @click="showMoreFields = !showMoreFields"
              >
                {{ moreFieldsOpen ? 'Fewer fields' : `More fields (${morePalette.length})` }}
                <template #icon>
                  <n-icon :component="moreFieldsOpen ? IconChevronUp : IconChevronDown" />
                </template>
              </gaia-button>
              <gaia-text size="micro" tone="faint" :style="{ lineHeight: 1.5 }">
                <template v-if="activeParty">
                  Pick a field, then click the page. It goes to
                  {{ partyDisplayName(activeParty) }}.
                </template>
                <template v-else>Pick a signer above, then a field, then click the page.</template>
                <template v-if="!canAssignSignatureField">
                  Signature fields need an active signer.
                </template>
              </gaia-text>
            </n-flex>
          </gaia-card>

          <!-- What is on this document, page by page. Selecting a field unfolds
               its own settings in place, so there is no separate detail card. -->
          <gaia-card content-style="padding: 12px">
            <n-flex vertical :size="8" :wrap="false">
              <n-flex align="baseline" :size="6" :wrap="false">
                <gaia-text size="dense" weight="bold" tone="ink">Placed fields</gaia-text>
                <gaia-text v-if="fieldsForCurrentDocument.length" size="micro" tone="faint">
                  {{ fieldsForCurrentDocument.length }}
                </gaia-text>
              </n-flex>
              <gaia-text v-if="placedGroups.length === 0" size="micro" tone="faint">
                Fields you place on this document appear here.
              </gaia-text>
              <n-flex
                v-for="group in placedGroups"
                :key="group.page"
                vertical
                :size="5"
                :wrap="false"
              >
                <gaia-text variant="label" size="nano" tone="subtle"
                  >Page {{ group.page }}</gaia-text
                >
                <!-- Each field is a small card: the row is its header, and a field
                     with settings unfolds them into the card's lower panel. -->
                <div
                  v-for="field in group.fields"
                  :key="field.uid"
                  class="placed"
                  :class="{ open: selectedField?.uid === field.uid }"
                >
                  <div
                    class="placed-head"
                    role="button"
                    tabindex="0"
                    :aria-expanded="
                      fieldHasSettings(field) ? selectedField?.uid === field.uid : undefined
                    "
                    @click="selectField(selectedField?.uid === field.uid ? null : field.uid)"
                    @keydown.enter.prevent="
                      selectField(selectedField?.uid === field.uid ? null : field.uid)
                    "
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                      :stroke="partyColor(field.recipient_id)"
                      stroke-width="1.4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      :style="{ flex: 'none' }"
                    >
                      <path :d="iconPathFor(fieldPaletteLabel(field))" />
                    </svg>
                    <n-flex vertical :size="0" :wrap="false" :style="{ flex: 1, minWidth: 0 }">
                      <gaia-text size="control" weight="semibold" tone="ink" truncate>
                        {{ fieldPaletteLabel(field) }}
                      </gaia-text>
                      <gaia-text size="micro" tone="subtle" truncate>
                        {{ fieldRecipientName(field) }}
                      </gaia-text>
                    </n-flex>
                    <n-icon
                      v-if="fieldHasSettings(field)"
                      :component="IconChevronDown"
                      :size="14"
                      class="placed-chevron"
                    />
                    <gaia-icon-button
                      :dimension="22"
                      quaternary
                      title="Remove field"
                      @click.stop="removeField(field.uid)"
                    >
                      <n-icon :component="IconClose" :size="13" />
                    </gaia-icon-button>
                  </div>

                  <!-- Label beside control: one line per setting keeps the panel short. -->
                  <n-flex
                    v-if="selectedField?.uid === field.uid && fieldHasSettings(field)"
                    vertical
                    :size="8"
                    :wrap="false"
                    class="placed-body"
                  >
                    <n-flex v-if="signerParties.length > 1" align="center" :size="8" :wrap="false">
                      <gaia-text size="micro" tone="subtle" class="setting-label">Signer</gaia-text>
                      <gaia-select
                        :value="field.recipient_id"
                        :options="signerSelectOptions"
                        size="tiny"
                        placeholder="Select signer"
                        :style="{ flex: 1, minWidth: 0 }"
                        @update:value="reassignSelectedField"
                      />
                    </n-flex>

                    <n-flex
                      v-if="field.type === SignatureFieldType.MENTION"
                      align="center"
                      :size="8"
                      :wrap="false"
                    >
                      <gaia-text size="micro" tone="subtle" class="setting-label"
                        >Mention</gaia-text
                      >
                      <gaia-input
                        :value="field.mention ?? ''"
                        size="tiny"
                        placeholder="Mention text"
                        :style="{ flex: 1, minWidth: 0 }"
                        @update:value="value => updateSelectedField({ mention: value })"
                      />
                    </n-flex>

                    <n-flex
                      v-if="field.type === SignatureFieldType.TEXT && !isPresetText(field)"
                      align="center"
                      :size="8"
                      :wrap="false"
                    >
                      <gaia-text size="micro" tone="subtle" class="setting-label"
                        >Question</gaia-text
                      >
                      <gaia-input
                        :value="field.question ?? ''"
                        size="tiny"
                        placeholder="What the signer fills in"
                        :style="{ flex: 1, minWidth: 0 }"
                        @update:value="value => updateSelectedField({ question: value })"
                      />
                    </n-flex>

                    <n-flex
                      v-if="field.type === SignatureFieldType.TEXT"
                      align="center"
                      :size="8"
                      :wrap="false"
                    >
                      <gaia-text size="micro" tone="subtle" class="setting-label">
                        Max length
                      </gaia-text>
                      <gaia-input-number
                        :value="field.max_length ?? DEFAULT_TEXT_MAX_LENGTH"
                        :min="1"
                        :precision="0"
                        :show-button="false"
                        size="tiny"
                        :style="{ flex: 1, minWidth: 0 }"
                        @update:value="
                          value =>
                            updateSelectedField({
                              max_length:
                                typeof value === 'number' ? value : DEFAULT_TEXT_MAX_LENGTH,
                            })
                        "
                      >
                        <template #suffix>characters</template>
                      </gaia-input-number>
                    </n-flex>

                    <n-flex
                      v-if="field.type === SignatureFieldType.READ_ONLY_TEXT"
                      align="center"
                      :size="8"
                      :wrap="false"
                    >
                      <gaia-text size="micro" tone="subtle" class="setting-label">Text</gaia-text>
                      <gaia-input
                        :value="field.text ?? ''"
                        size="tiny"
                        placeholder="Read-only text"
                        :style="{ flex: 1, minWidth: 0 }"
                        @update:value="value => updateSelectedField({ text: value })"
                      />
                    </n-flex>

                    <n-flex
                      v-if="field.type === SignatureFieldType.SIGNER_NAME"
                      align="center"
                      :size="8"
                      :wrap="false"
                    >
                      <gaia-text size="micro" tone="subtle" class="setting-label">Format</gaia-text>
                      <gaia-select
                        :value="field.name_format ?? 'full_name'"
                        :options="NAME_FORMAT_OPTIONS"
                        size="tiny"
                        :style="{ flex: 1, minWidth: 0 }"
                        @update:value="
                          value => {
                            if (
                              value === 'first_name' ||
                              value === 'last_name' ||
                              value === 'full_name'
                            ) {
                              updateSelectedField({ name_format: value });
                            }
                          }
                        "
                      />
                    </n-flex>

                    <n-flex
                      v-if="isTextStyleField(field.type)"
                      align="center"
                      :size="8"
                      :wrap="false"
                    >
                      <gaia-text size="micro" tone="subtle" class="setting-label">
                        Font size
                      </gaia-text>
                      <gaia-select
                        :value="field.font_size ?? DEFAULT_FIELD_FONT_SIZE_PT"
                        :options="FONT_SIZE_OPTIONS"
                        size="tiny"
                        :style="{ flex: 1, minWidth: 0 }"
                        @update:value="
                          value => {
                            if (typeof value === 'number')
                              updateSelectedField({ font_size: value });
                          }
                        "
                      />
                    </n-flex>

                    <!-- Try an answer: the box on the page shows it at its real size. -->
                    <n-flex
                      v-if="field.type === SignatureFieldType.TEXT"
                      align="center"
                      :size="8"
                      :wrap="false"
                    >
                      <gaia-text size="micro" tone="subtle" class="setting-label">
                        Preview with
                      </gaia-text>
                      <gaia-input
                        :value="
                          (previewSamples[field.uid] ?? '').slice(
                            0,
                            field.max_length ?? DEFAULT_TEXT_MAX_LENGTH
                          )
                        "
                        size="tiny"
                        :placeholder="defaultPreview(field)"
                        :maxlength="field.max_length ?? DEFAULT_TEXT_MAX_LENGTH"
                        show-count
                        clearable
                        :style="{ flex: 1, minWidth: 0 }"
                        @update:value="value => setPreviewSample(field.uid, value)"
                      />
                    </n-flex>

                    <!-- Whether the text fits its box, and the one-click fix. -->
                    <n-flex v-if="selectedFit" align="baseline" :size="[8, 2]">
                      <gaia-text size="micro" :tone="selectedFit.warn ? 'warning' : 'subtle'">
                        {{ selectedFit.message }}
                      </gaia-text>
                      <gaia-link
                        v-if="selectedFit.action"
                        @click="updateSelectedField(selectedFit.action.patch)"
                      >
                        {{ selectedFit.action.label }}
                      </gaia-link>
                    </n-flex>
                  </n-flex>
                </div>
              </n-flex>
            </n-flex>
          </gaia-card>
        </n-flex>
      </n-gi>
    </n-grid>
  </n-flex>
</template>

<style scoped>
/* The step fills the wizard's column (WizardLayout `fill`): the chips and
   banners keep their height, the grid takes the rest. */
.fields-step {
  flex: 1;
  min-height: 0;
}

.fields-pane {
  min-height: 0;
}

/* A placed field: a small card whose header row toggles a settings panel.
   Naive has no compact collapsible card, so the shell, hover, chevron turn
   and the panel's surface are ours. */
.placed {
  border: 1px solid v-bind('gaiaNeutral.borderCard');
  border-radius: 8px;
  background: v-bind('gaiaNeutral.surface');
  overflow: hidden;
  transition: border-color 0.15s ease;
}

.placed.open {
  border-color: v-bind('gaiaNeutral.borderHoverSoft');
}

.placed-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 6px 6px 10px;
  cursor: pointer;
}

.placed-head:hover,
.placed-head:focus-visible {
  background: v-bind('gaiaNeutral.surfaceSoft');
  outline: none;
}

.placed-chevron {
  flex: none;
  color: v-bind('gaiaNeutral.textSubtle');
  transition: transform 0.15s ease;
}

.placed.open .placed-chevron {
  transform: rotate(180deg);
}

.placed-body {
  padding: 10px;
  border-top: 1px solid v-bind('gaiaNeutral.borderDivider');
  background: v-bind('gaiaNeutral.surfaceSoft');
}

.setting-label {
  flex: none;
  width: 64px;
}

@media (prefers-reduced-motion: reduce) {
  .placed,
  .placed-chevron {
    transition: none;
  }
}

.fields-sidebar {
  min-height: 0;
  overflow: auto;
}

/* The document's frame. The preview inside does the scrolling and draws the
   pages on its own tinted canvas. */
.page-frame {
  flex: 1;
  min-height: 0;
  border: 1px solid v-bind('gaiaNeutral.borderCard');
  border-radius: 4px;
  background: v-bind('gaiaNeutral.surface');
  box-shadow: v-bind('gaiaShadow.card');
  overflow: hidden;
}
</style>
